// Errores del cierre de la venta (complete-sale) → qué lee el vendedor, qué pieza se marca y si
// reintentar sirve de algo.
import { ApiError } from '@/api/errors';
import { wizardErrorMsg } from './api/wizardError';
import { integrationFailure } from './integrationErrors';
import { identifierMismatchOf, lineErrorMessage } from './lineErrors';
import {
    PROMOTION_NOT_REVALIDATED,
    PROMOTION_NOT_REVALIDATED_TOAST,
} from './promotionRevalidation';

export interface CompleteSaleFailure {
    message: string;
    /** Línea y SKU cuyas piezas hay que volver a capturar; ausente si el silo no dice cuál. */
    target?: { lineId: string; sku: string };
    /** Los identificadores son el problema: el mensaje va junto a las piezas. */
    aboutPieces: boolean;
    canRetry: boolean;
    /** Las líneas en pantalla ya no son las de la venta: hay que releer la sesión antes de reintentar. */
    reloadSession: boolean;
    /** Una promo quedó sin revalidar: hay que regresar a Arma la venta. */
    backToBuildSale?: true;
}

const RESERVATION_NOT_FOUND_MESSAGE =
    'La venta ya está cobrada, pero lo apartado ya no está en el inventario. No la vuelvas a cobrar: repórtala a soporte para que revisen el inventario.';
const RESERVATION_EXPIRED_MESSAGE =
    'La venta ya está cobrada, pero lo apartado venció antes de cerrarla. No la vuelvas a cobrar: repórtala a soporte para que revisen el inventario.';
const CATALOG_DOWN_MESSAGE = 'El inventario no respondió; vuelve a intentar en un momento.';
const SALE_CHANGED_MESSAGE =
    'Los productos de la venta cambiaron. Recarga la venta, revisa las piezas y vuelve a intentar.';
const SESSION_CLOSED_MESSAGE = 'La venta ya no está abierta. Recarga la venta para ver cómo quedó.';
const FALLBACK = 'No se pudo registrar la venta.';

const PIECE_CODES = new Set(['IDENTIFIER_MISMATCH', 'IDENTIFIER_NOT_AVAILABLE']);
const SALE_CHANGED_CODES = new Set(['LINE_NOT_FOUND', 'LINE_SKU_MISMATCH']);

export function completeSaleFailure(error: unknown): CompleteSaleFailure {
    const code = error instanceof ApiError ? error.code : null;
    if (code === 'RESERVATION_NOT_FOUND') {
        return {
            message: RESERVATION_NOT_FOUND_MESSAGE,
            aboutPieces: false,
            canRetry: false,
            reloadSession: false,
        };
    }
    // Con la venta cobrada, la reserva vencida es una anomalía: se escala igual que la ausente.
    if (code === 'RESERVATION_EXPIRED') {
        return {
            message: RESERVATION_EXPIRED_MESSAGE,
            aboutPieces: false,
            canRetry: false,
            reloadSession: false,
        };
    }
    // Pieza perdida: se relee la venta para que la línea salga marcada con su acción.
    const integration = integrationFailure(error);
    if (integration) {
        return {
            message: integration.message,
            aboutPieces: false,
            canRetry: false,
            reloadSession: integration.recovery === 'build-sale',
        };
    }
    if (code === PROMOTION_NOT_REVALIDATED) {
        return {
            message: PROMOTION_NOT_REVALIDATED_TOAST,
            aboutPieces: false,
            canRetry: false,
            reloadSession: false,
            backToBuildSale: true,
        };
    }
    if (code === 'CATALOG_UNAVAILABLE') {
        return {
            message: CATALOG_DOWN_MESSAGE,
            aboutPieces: false,
            canRetry: true,
            reloadSession: false,
        };
    }
    // Perdió la carrera del cierre o la venta se canceló: se relee la sesión y no se reintenta.
    if (code === 'INVALID_SESSION_STATE') {
        return {
            message: SESSION_CLOSED_MESSAGE,
            aboutPieces: false,
            canRetry: false,
            reloadSession: true,
        };
    }
    if (code !== null && SALE_CHANGED_CODES.has(code)) {
        return {
            message: SALE_CHANGED_MESSAGE,
            aboutPieces: false,
            canRetry: true,
            reloadSession: true,
        };
    }
    if (code !== null && PIECE_CODES.has(code)) {
        return {
            message: lineErrorMessage(error) ?? FALLBACK,
            target: identifierMismatchOf(error),
            aboutPieces: true,
            canRetry: true,
            reloadSession: false,
        };
    }
    return {
        message: lineErrorMessage(error) ?? wizardErrorMsg(error, FALLBACK),
        aboutPieces: false,
        canRetry: true,
        reloadSession: false,
    };
}
